import { useEffect, useState } from "react";
import { Box, Flex, Image, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { GoDotFill } from "react-icons/go";

import { Button } from "MVVM/Components/Button";
import { URLS } from "MVVM/Utilities/Maps";
import { analytics } from "MVVM/Analytics";
import theme from "theme/themeThree";
import { FaRegBell, FaStar } from "react-icons/fa6";
import { Checkbox } from "MVVM/Components/Checkbox";
import { Divider, DividerAlt } from "MVVM/Components/Divider";
import { Link } from "MVVM/Components/Link";
import { Tooltip } from "MVVM/Components/Tooltip";
import { MdOutlineStar } from "react-icons/md";
import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";

export const SubscribeToPlayer = ({
  playerName,
  playerPhoto,
  teamPhoto,
}: {
  playerName: string;
  playerPhoto: string;
  teamPhoto: string;
}) => {
  const [isSuperfan, setIsSuperfan] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { track } = useAnalyticsContext();

  useEffect(() => {
    if (playerName) {
      track("Component Viewed", {
        locationIdentifier: "subscribe_player_modal",
        location: "clip_experience",
        featureGroup: "subscribe_player",
        playerName: playerName,
      });
    } else {
      track("Component Viewed", {
        locationIdentifier: "subscribe_hltv_clips_modal",
        location: "clip_experience",
        featureGroup: "subscribe_to_hltv_clips",
      });
    }
  }, [playerName, track]);

  return (
    <StyledBox>
      <HeaderImages
        style={{
          backgroundImage: `linear-gradient(to bottom, transparent, var(--Gray-750, #1c2a34)), url(${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/ctas/cs.jpg)`,
        }}
      >
        {playerPhoto ? (
          <PlayerImage src={playerPhoto} alt={playerName} />
        ) : (
          <HLTVImage
            src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/brand/HLTV.png`}
            alt={playerName}
          />
        )}
        <TeamImage src={teamPhoto} alt={playerName} />
        <GradientFooter />
      </HeaderImages>
      <Flex
        flexDirection="column"
        padding={7}
        paddingTop={2}
        justifyContent={"space-between"}
        alignItems={"center"}
        style={{ gap: 20, backgroundColor: "#1c2a34" }}
      >
        <Flex flexDirection="column">
          <Text variant="text.headingMedium" textAlign={"center"} mb={3}>
            Subscribe to{" "}
            {playerName ? (
              <span style={{ color: theme.colors.envy }}>{playerName}</span>
            ) : (
              <>
                <span style={{ color: theme.colors.envy }}>HLTV Clips</span> by
                Allstar
              </>
            )}
          </Text>
          <Text
            variant="text.paragraphLarge"
            textAlign={"center"}
            style={{ fontWeight: 500, lineHeight: "28px", fontSize: "18px" }}
          >
            {playerName ? (
              <>
                Get notified about their{" "}
                <PartnerImage
                  src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/partners/Branding.png`}
                />
                clips, matches & activity
              </>
            ) : (
              <>
                Get notified about the latest pro CS2 clips, matches & activity
              </>
            )}
          </Text>
        </Flex>
        {isSubscribed && isSuperfan && (
          <SuperfanContainer>
            <Text
              variant={"text.headingXSmall"}
              textAlign={"center"}
              color="envy"
              fontWeight={700}
            >
              Your Superfan status is pending!
            </Text>
            <Text
              variant={"text.paragraphLarge"}
              textAlign={"center"}
              color="chalk"
            >
              We’ll let <span style={{ fontWeight: "bold" }}>{playerName}</span>{" "}
              know someone wants to become a Superfan. Once confirmed, you will
              be notified to enter payment details. Cancel any time.
            </Text>
          </SuperfanContainer>
        )}
        {!isSubscribed ? (
          <List />
        ) : (
          <DividerAlt
            width="100%"
            style={{ color: theme.colors.chalk }}
            my={0}
          />
        )}

        {!playerName && !isSubscribed && (
          <SubscribeButton
            onClick={() => {
              track("Button - Click", {
                locationIdentifier: "subscribe_to_hltv_clips_button",
                location: "clip_experience",
                featureGroup: "subscribe_to_hltv_clips",
              });
              setIsSubscribed(true);
            }}
            style={{ gap: 8, flexShrink: 0 }}
          >
            <FaRegBell />
            Subscribe to HLTV Clips
          </SubscribeButton>
        )}

        {!isSubscribed && playerName && (
          <SubscribeContainer>
            <SubscribeButton
              onClick={() => {
                track("Button - Click", {
                  locationIdentifier: "subscribe_to_player_button",
                  location: "clip_experience",
                  featureGroup: "subscribe_player",
                  isSuperfan: isSuperfan,
                  playerName: playerName,
                });
                setIsSubscribed(true);
              }}
              style={{ gap: 8, flexShrink: 0 }}
            >
              <FaRegBell />
              Subscribe to {playerName}
            </SubscribeButton>

            <Flex style={{ gap: 15, alignItems: "center" }}>
              <Box
                style={{
                  flexShrink: 0,
                  position: "relative",
                  width: "20px",
                  height: "30px",
                }}
              >
                <Checkbox
                  checked={isSuperfan}
                  onChange={() => {
                    setIsSuperfan(!isSuperfan);
                  }}
                  onClick={() => {
                    setIsSuperfan(!isSuperfan);
                  }}
                  error={false}
                />
              </Box>

              <Text
                variant={"text.labelSmall"}
                style={{
                  lineHeight: "18px",
                  fontSize: "12px",
                  width: "200px",
                  textAlign: "left",
                }}
              >
                Yes, I'd like to become a{" "}
                <span style={{ color: theme.colors.envy, fontWeight: "bold" }}>
                  "Superfan"
                </span>{" "}
                of <span style={{ fontWeight: "bold" }}>{playerName}</span> for{" "}
                <span style={{ fontWeight: "bold" }}>$0.99/month</span>. 100% of
                your subscription goes to the player.
              </Text>
            </Flex>
          </SubscribeContainer>
        )}

        {isSubscribed && (
          <Flex
            flexDirection="column"
            style={{ gap: 24 }}
            justifyContent="center"
            alignItems="center"
            width="80%"
          >
            <Text
              variant={"text.labelLarge"}
              style={{ fontWeight: 500, lineHeight: "28px", fontSize: "18px" }}
            >
              Want earlier access? Get the{" "}
              <span style={{ fontWeight: "bold" }}>Allstar app</span>
            </Text>
            <MobileLinks>
              <Link
                href="https://apps.apple.com/us/app/allstar-play-share-star/id6476161266"
                target="_blank"
              >
                <StyledMobileButton
                  src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/mobile/appstorechooser/ios.png`}
                  alt="App Store"
                />
              </Link>
              <Link
                href="https://play.google.com/store/apps/details?id=com.androidapp.gg.allstar&hl=en_US&gl=US"
                target="_blank"
              >
                <StyledMobileButton
                  src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/mobile/appstorechooser/android.png`}
                  alt="Google Play Store"
                />
              </Link>
            </MobileLinks>
          </Flex>
        )}

        {playerName && (
          <Flex style={{ gap: 10 }}>
            <Text variant={"text.labelMedium"} color="chalk">
              What's a Superfan?
            </Text>

            <Tooltip
              tooltipContent={
                <Flex
                  flexDirection="column"
                  style={{
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "-10px",
                      left: "70%",
                      width: "20px",
                      height: "20px",
                      backgroundColor: "#3883C0",
                      transform: "translateX(-50%) rotate(45deg)",
                      zIndex: 1,
                    }}
                  />

                  <Flex
                    flexDirection="row"
                    style={{
                      gap: 8,
                      padding: 12,
                      width: 350,
                      background:
                        "linear-gradient(225deg, #1D95C5 0%, #824DB1 100%)",
                      borderRadius: 16,
                      position: "relative",
                      zIndex: 2,
                    }}
                  >
                    <MdOutlineStar
                      size={24}
                      style={{
                        flexShrink: 0,
                        color: theme.colors.ice,
                        marginTop: -2,
                      }}
                    />
                    <Flex flexDirection="column" style={{ gap: 8 }}>
                      <Text
                        variant="text.labelMedium"
                        color="ice"
                        style={{ fontWeight: 600 }}
                      >
                        Superfan Perks
                      </Text>
                      <Text
                        variant="text.paragraphSmall"
                        style={{ lineHeight: "1.4", fontWeight: 500 }}
                        color="ice"
                      >
                        Unlock the "Superfan" profile badge, limited edition
                        Signature, priority placement in Comments & exclusive
                        discounts. 100% of your money goes to the player.
                      </Text>
                    </Flex>{" "}
                  </Flex>
                </Flex>
              }
              type="click"
              align="center"
              wrapText={true}
              padding={0}
              position={{
                left: "-210%",
                bottom: "-180px",
              }}
            >
              <LearnMoreText variant={"text.labelMedium"} color="envy" mr={4}>
                Learn more
              </LearnMoreText>
            </Tooltip>
          </Flex>
        )}
      </Flex>
    </StyledBox>
  );
};

const List = () => {
  return (
    <Flex flexDirection="column" style={{ gap: 5 }}>
      <Row>
        <StyledBullet color="chalk" />
        <ListItemText variant={"text.paragraphMedium"}>
          Support your favorite players and teams
        </ListItemText>
      </Row>
      <Row>
        <StyledBullet color="chalk" />
        <ListItemText variant={"text.paragraphMedium"}>
          Get notified as soon as their latest matches are available
        </ListItemText>
      </Row>
      <Row>
        <StyledBullet color="chalk" />
        <ListItemText variant={"text.paragraphMedium"}>
          Watch their latest clips first before anybody else
        </ListItemText>
      </Row>
      <Row>
        <StyledBullet color="chalk" />
        <ListItemText variant={"text.paragraphMedium"}>
          Never miss a chance to be "first" in their comments
        </ListItemText>
      </Row>
      <Row>
        <StyledBullet color="chalk" />
        <ListItemText variant={"text.paragraphMedium"}>
          Early access to new features, exclusive content and more
        </ListItemText>
      </Row>
    </Flex>
  );
};

const SubscribeContainer = styled(Flex)`
  flex-direction: row;
  gap: 16px;
  width: 100%;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 450px) {
    flex-direction: column;
  }
`;
const GradientFooter = styled(Flex)`
  background: linear-gradient(to bottom, transparent, #1c2a34);
  height: 75px;
  width: 100%;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2;
`;

const SuperfanContainer = styled(Flex)`
  background: #00000080;
  flex-direction: column;
  gap: 16px;
  border-radius: 16px;
  width: 100%;
  padding-top: 22px;
  padding-bottom: 22px;
  padding-right: 36px;
  padding-left: 36px;
`;

const StyledMobileButton = styled(Image)`
  width: 150px;
`;

const MobileLinks = styled(Flex)`
  flex-wrap: wrap;
  width: 100%;
  justify-content: space-around;
  row-gap: 8px;
  a {
    flex-shrink: 0;
  }
`;
const SubscribeButton = styled(Flex)`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.envy};
  border-radius: 20px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  line-height: 18px;
  height: 40px;
  gap: 16px;
  padding-bottom: 8px;
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 8px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.envyAlpha80};
  }
`;

const PartnerImage = styled(Image)`
  height: 25px;
  display: inline-block;
  vertical-align: top;
  margin: 2px 4px;
`;

const LearnMoreText = styled(Text)`
  cursor: pointer;
`;

const StyledBullet = styled(GoDotFill)`
  color: ${({ theme }) => theme.colors.chalk};
  fill: ${({ theme }) => theme.colors.chalk};
  flex-shrink: 0;
  height: 16px;
  width: 16px;
`;
const StyledBox = styled(Box)`
  background-color: #10161a;
  border-color: #233438;
  border-radius: 16px;
  border-style: solid;
  border-width: 1px;
  box-shadow: 0 0 30px #233438;
  max-width: 550px;
  mix-blend-mode: normal;
  width: 100vw;
`;

const Row = styled(Flex)`
  align-items: center;
  gap: 8px;
  justify-content: start;
`;

const ListItemText = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 16px;
  line-height: 24px;
`;

const HeaderImages = styled(Flex)`
  background-size: cover;
  border-top-left-radius: 14px;
  border-top-right-radius: 14px;
  height: 192px;
  position: relative;
  width: 100%;
`;

const PlayerImage = styled(Image)`
  bottom: 0;
  height: 180px;
  left: 25%;
  position: absolute;
  z-index: 1;
`;

const HLTVImage = styled(Image)`
  bottom: 0;
  left: 50%;
  position: absolute;
  transform: translate(-50%, -100%);
  width: 200px;
  z-index: 1;
`;

const TeamImage = styled(Image)`
  bottom: 5%;
  height: 150px;
  position: absolute;
  right: 23%;
`;
